import React from "react";

interface ActionDescriptor {
  type: string;
  label: string;
  payload?: Record<string, unknown>;
}

function isActionDescriptor(value: unknown): value is ActionDescriptor {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return typeof obj.type === "string" && typeof obj.label === "string";
}

interface ActionsPanelBlockProps {
  payload: Record<string, unknown>;
  /**
   * Optional click handler. When provided, buttons dispatch (type, payload)
   * to the host. When absent, buttons render as visual-only descriptors —
   * matches the sandbox render-spec compiler convention of relying on
   * data-agent-action-* attributes for the postMessage bridge.
   */
  onAction?: (action: ActionDescriptor) => void;
}

/**
 * Call-to-action button cluster. Payload shape mirrors the workspace
 * render-spec compiler:
 *   { actions: Array<{ type, label, payload? }> }
 *
 * Visual spec: primary button styling matches .btn / .btn-primary in
 * sandbox-styles.css.
 */
export default function ActionsPanelBlock({ payload, onAction }: ActionsPanelBlockProps) {
  const rawActions = Array.isArray(payload.actions) ? payload.actions : [];
  const actions = rawActions.filter(isActionDescriptor);

  if (actions.length === 0) {
    return (
      <div
        aria-label="actions-panel-empty"
        className="text-xs italic"
        style={{ color: "var(--c-text-faint)" }}
      >
        No actions available.
      </div>
    );
  }

  return (
    <div aria-label="actions-panel" className="flex flex-row flex-wrap" style={{ gap: 8 }}>
      {actions.map((action, idx) => {
        const payloadJson = action.payload ? JSON.stringify(action.payload) : undefined;
        return (
          <button
            key={`${action.type}-${idx}`}
            type="button"
            data-agent-action-type={action.type}
            data-agent-action-payload={payloadJson}
            onClick={onAction ? () => onAction(action) : undefined}
            className="font-medium"
            style={{
              padding: "8px 14px",
              borderRadius: 6,
              fontSize: 13,
              backgroundColor: "var(--c-primary)",
              color: "var(--c-primary-text)",
              border: "1px solid var(--c-primary)",
              cursor: onAction ? "pointer" : "default",
              transition: "background-color 150ms ease",
            }}
          >
            {action.label}
          </button>
        );
      })}
    </div>
  );
}
